import Highcharts from 'highcharts';
import styled from 'styled-components';
import {
  MetricsDetails,
  getTimePeriodText,
} from 'modules/missionControl/lib/components/MetricsDetails';
import { LineChart } from 'modules/missionControl/lib/components/charts/LineChart';
import { useMissionControlContext } from 'modules/missionControl/contexts/MissionControlContext';
import { calculateDelta } from 'modules/missionControl/utils/calculations';
import { useMemo, useState } from 'react';
import { Controlbar } from 'ui/Controlbar';
import { ALIGNMENT } from 'ui/selects';
import { ChevronDown16 } from 'ui/icons/aurora/16px/ChevronDown16';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { Menu } from 'ui/menu';
import { spacing } from 'ui/theme';
import { TOTAL_SENT_METRIC_LABEL } from '../CampaignsHealthMetrics.constants';
import { METRICS_LOADING_DATA } from './MetricsCharts.constants';
import {
  getCrossMediumChartData,
  getMetricsChartOptions,
  getMetricOptions,
} from './MetricsCharts.utils';

const StyledChart = styled(LineChart)`
  width: 100%;
`;

const StyledButton = styled(Button)`
  margin-left: -${spacing.half};
  padding: ${spacing.half} ${spacing.quarter};
`;

const chartOptions = getMetricsChartOptions();

export const MetricsCharts = () => {
  const [selectedMetric, setSelectedMetric] = useState(TOTAL_SENT_METRIC_LABEL);

  const {
    campaignsHealthMetricsQuery: {
      isLoading,
      metrics: { totalMetricsAggregations = {}, prevTotalMetricsAggregations = {} } = {},
    },
    timezone,
    dateRangePreset,
  } = useMissionControlContext();

  const allChartData = useMemo(
    () => getCrossMediumChartData(totalMetricsAggregations),
    [totalMetricsAggregations],
  );
  const metricOptions = useMemo(() => getMetricOptions(allChartData), [allChartData]);
  const selectedMetricData = allChartData[selectedMetric] || METRICS_LOADING_DATA;
  const selectedMetricCurrentValue = totalMetricsAggregations[selectedMetric]?.value;
  const selectedMetricPrevValue = prevTotalMetricsAggregations[selectedMetric]?.value;

  const delta = calculateDelta(selectedMetricCurrentValue, selectedMetricPrevValue);

  return (
    <>
      <div>
        <Controlbar aria-label="Sends metrics dashboard control" variant="ghost">
          <Menu
            id={'metric-select'}
            filterInputPlaceholder="Search for a metric"
            options={metricOptions.map((option) => ({
              label: option.label as string,
              id: option.id,
            }))}
            isDisabled={isLoading}
            defaultSelectedOptionIds={[selectedMetric]}
            verticalAlignment={ALIGNMENT.DOWN}
            horizontalAlignment={ALIGNMENT.LEFT}
            handleSelectionChange={(selected: string) => {
              setSelectedMetric(selected);
            }}
          >
            <StyledButton variant={BUTTON_VARIANTS.GHOST} size={BUTTON_SIZES.SMALL}>
              {selectedMetric}
              <ChevronDown16 />
            </StyledButton>
          </Menu>
        </Controlbar>

        <MetricsDetails
          metricValue={selectedMetricCurrentValue}
          metricName={selectedMetric}
          delta={delta}
          timePeriod={getTimePeriodText(dateRangePreset)}
        />
      </div>
      <StyledChart
        data={selectedMetricData.data as Highcharts.SeriesOptionsType[]}
        chartOptions={chartOptions}
        timezone={timezone}
      />
    </>
  );
};
